import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { SaveWinner } from "../../api/api";
import {
  FloorStatus,
  GetFloorGames,
  GetFloorStatus,
  StartFloorRun,
  FLOOR_GAME_ID,
  isFloorAuthError,
} from "../../api/floorApi";
import {
  LavaMatchConfig,
  LavaMatchState,
  loadLavaMatch,
  saveLavaMatch,
} from "../../lavaMatch";
import { FloorSettings } from "./FloorSettings";
import {
  BigTime,
  ButtonsContainer,
  ContainerTitle,
  FloorStatusLine,
  GameInfoTitle,
  HeaderTitlesContainer,
  LavaFloorPageContainer,
  LevelArrow,
  LevelName,
  LevelSelectorContainer,
  Message,
  MiddleContainer,
  PrimaryButton,
  ScoreChips,
  SecondaryButton,
  SettingsToggle,
  SubInfo,
  TeamCard,
  TeamsRow,
} from "./LavaFloorPageStyled";

// Referee flow for "The floor is lava": per round each team plays the same
// Time Trial level on the LED floor; the LOWEST final time (elapsed +
// penalties, computed by the floor) wins the round. The floor is driven
// remotely through the launcher API (see src/api/floorApi.ts).

type TeamKey = "yellow" | "green";
type Phase = "level" | "team" | "playing" | "roundResult";

const DEFAULT_LEVELS = ["DEMO", "Level 1", "RUN", "New Level", "Criss Cross",
  "Snake", "Rope", "Love", "Jump"];

export const LavaFloorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const uuid = localStorage.getItem("uuid");
  const { gameId, gameType, gameName, language } = location.state;
  const ro = language === "RO";
  // NEW lava QR mode: the QR fixed the level of every round (round N plays
  // lava.levels[N-1]) and a played round can never be replayed — progress is
  // persisted per device, so back/reload/re-scan RESUMES the match.
  const lava: LavaMatchConfig | null = (location.state as any).lava || null;
  const fixed = !!lava;
  const [saved] = useState<LavaMatchState | null>(() =>
    fixed ? loadLavaMatch(gameId) : null
  );

  const winsNeeded = (() => {
    const t = String(gameType || "").replace(/ /g, "_");
    if (t === "Best_of_3") return 2;
    if (t === "Best_of_5") return 3;
    return 1;
  })();

  const [levels, setLevels] = useState<string[]>(
    fixed && lava!.levels.length ? lava!.levels : DEFAULT_LEVELS
  );
  const [phase, setPhase] = useState<Phase>(() =>
    saved && !saved.done &&
    (saved.times.yellow != null || saved.times.green != null)
      ? "team" // mid-round: one team already has a time — resume there
      : "level"
  );
  const [round, setRound] = useState(saved?.round || 1);
  const [levelIdx, setLevelIdx] = useState(
    fixed ? ((saved?.round || 1) - 1) % lava!.levels.length : 0
  );
  const [scoreStatus, setScoreStatus] = useState(
    saved?.scoreStatus || { yellowTeam: 0, greenTeam: 0 }
  );
  const [times, setTimes] = useState<{
    yellow: number | null;
    green: number | null;
  }>(saved?.times || { yellow: null, green: null });
  // the match was already decided on this device — locked for good
  const [matchDone] = useState<boolean>(!!saved?.done);
  const [currentTeam, setCurrentTeam] = useState<TeamKey | null>(null);
  const [floor, setFloor] = useState<FloorStatus | null>(null);
  const [floorOnline, setFloorOnline] = useState(false);
  const [authFailed, setAuthFailed] = useState(false); // launcher said 401
  const [message, setMessage] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  // bumps when URL/token are saved -> polls restart with the new settings
  const [cfgVersion, setCfgVersion] = useState(0);

  // tag of the run in progress; sawActive = the floor was seen counting
  // down / running for THIS run (distinguishes "finished" from "aborted")
  const runTag = useRef<string | null>(null);
  const sawActive = useRef(false);

  const teamName = (t: TeamKey) =>
    t === "yellow"
      ? ro
        ? "Echipa Portocalie"
        : "Team Orange"
      : ro
      ? "Echipa Verde"
      : "Team Green";

  // level names from the launcher (fallback: the known levels) — but a lava
  // QR match plays EXACTLY the QR's levels, so nothing may override them
  useEffect(() => {
    if (fixed) return;
    GetFloorGames()
      .then((games) => {
        const tt = games.find((g) => g.id === FLOOR_GAME_ID);
        if (tt && tt.levels && tt.levels.length) setLevels(tt.levels);
      })
      .catch(() => {});
  }, [cfgVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // floor status poll — fast while a run is on, slow otherwise
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const s = await GetFloorStatus();
        if (!stopped) {
          setFloor(s);
          setFloorOnline(true);
          setAuthFailed(false);
        }
      } catch (e) {
        if (!stopped) {
          setFloor(null);
          setFloorOnline(false);
          setAuthFailed(isFloorAuthError(e));
        }
      }
    };
    tick();
    const id = setInterval(tick, phase === "playing" ? 1000 : 3000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [phase, cfgVersion]);

  // react to floor updates while a run is on
  useEffect(() => {
    if (phase !== "playing" || !floor || !runTag.current || !currentTeam)
      return;
    if (floor.state === "countdown" || floor.state === "running")
      sawActive.current = true;

    const lr = floor.last_result;
    if (lr && lr.tag === runTag.current && typeof lr.score === "number") {
      // this run finished — the floor reports the final time in seconds
      runTag.current = null;
      const finished = currentTeam;
      const next = { ...times, [finished]: lr.score };
      setTimes(next);
      if (fixed) saveLavaMatch(gameId, { round, scoreStatus, times: next });
      if (next.yellow != null && next.green != null) {
        setPhase("roundResult");
      } else {
        setCurrentTeam(finished === "yellow" ? "green" : "yellow");
        setMessage(
          `${teamName(finished)}: ${lr.score}s — ${
            ro ? "urmează cealaltă echipă" : "other team is up next"
          }`
        );
        setPhase("team");
      }
    } else if (
      sawActive.current &&
      floor.state === "idle" &&
      (!lr || lr.tag !== runTag.current)
    ) {
      // someone stopped the run from the launcher — replay it
      runTag.current = null;
      setMessage(
        ro
          ? "Runda a fost oprită pe podea — pornește din nou"
          : "The run was stopped on the floor — start it again"
      );
      setPhase("team");
    }
  }, [floor]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRun = async () => {
    if (!currentTeam) return;
    const tag = `vu-${gameId}-r${round}-${currentTeam}-${uuidv4().slice(0, 8)}`;
    try {
      await StartFloorRun(levels[levelIdx] || null, tag);
      runTag.current = tag;
      sawActive.current = false;
      setMessage("");
      setPhase("playing");
    } catch {
      setMessage(
        ro
          ? "Nu pot porni podeaua — verifică adresa și rețeaua (⚙)"
          : "Cannot start the floor — check the address and network (⚙)"
      );
    }
  };

  const roundWinner: TeamKey | "draw" | null =
    times.yellow != null && times.green != null
      ? times.yellow === times.green
        ? "draw"
        : times.yellow < times.green
        ? "yellow"
        : "green"
      : null;

  const replayRound = () => {
    setTimes({ yellow: null, green: null });
    setCurrentTeam(null);
    setMessage("");
    setPhase("level");
  };

  const handleNextRound = () => {
    if (!roundWinner) return;
    const s = { ...scoreStatus };
    if (roundWinner === "draw") {
      s.yellowTeam += 1;
      s.greenTeam += 1;
    } else if (roundWinner === "yellow") s.yellowTeam += 1;
    else s.greenTeam += 1;
    setScoreStatus(s);

    const overall =
      s.yellowTeam >= winsNeeded && s.yellowTeam > s.greenTeam
        ? "YELLOW"
        : s.greenTeam >= winsNeeded && s.greenTeam > s.yellowTeam
        ? "GREEN"
        : null;
    if (overall && uuid && gameId != null) {
      if (fixed)
        saveLavaMatch(gameId, {
          round,
          scoreStatus: s,
          times,
          done: true, // decided: this match can never be played again
          winner: overall,
        });
      SaveWinner(uuid, gameId, overall).then(() => {
        setTimeout(() => {
          navigate("/winner-page", {
            state: {
              winnerTeam: overall,
              gameUniqueId: gameId,
              language: language,
            },
          });
        }, 500);
      });
      return;
    }
    if (fixed)
      saveLavaMatch(gameId, {
        round: round + 1,
        scoreStatus: s,
        times: { yellow: null, green: null },
      });
    setRound(round + 1);
    setLevelIdx((levelIdx + 1) % levels.length);
    setTimes({ yellow: null, green: null });
    setCurrentTeam(null);
    setMessage("");
    setPhase("level");
  };

  const onSettingsSaved = () => {
    setCfgVersion((v) => v + 1);
    setShowSettings(false);
  };

  const liveTime =
    floor && floor.state === "running" && floor.stats
      ? String(floor.stats["time"] || `${floor.score || 0}s`)
      : null;

  // lava QR match already decided on this device: locked for good
  if (matchDone) {
    return (
      <LavaFloorPageContainer>
        <HeaderTitlesContainer>
          <ContainerTitle>{gameName || "The floor is lava"}</ContainerTitle>
        </HeaderTitlesContainer>
        <MiddleContainer>
          <ContainerTitle>
            {ro ? "Meciul s-a jucat deja" : "This match was already played"}
          </ContainerTitle>
          {saved?.winner && (
            <SubInfo>
              {ro ? "Câștigător" : "Winner"}:{" "}
              {teamName(saved.winner === "YELLOW" ? "yellow" : "green")} (
              {scoreStatus.yellowTeam} - {scoreStatus.greenTeam})
            </SubInfo>
          )}
        </MiddleContainer>
        <ButtonsContainer>
          <PrimaryButton onClick={() => navigate("/home")}>
            <p>{ro ? "Înapoi la start" : "Back to Home"}</p>
          </PrimaryButton>
        </ButtonsContainer>
      </LavaFloorPageContainer>
    );
  }

  return (
    <LavaFloorPageContainer>
      {/* lava QR match: the QR is the configuration — no manual ⚙ */}
      {!fixed && (
        <SettingsToggle onClick={() => setShowSettings(!showSettings)}>
          ⚙
        </SettingsToggle>
      )}
      <HeaderTitlesContainer>
        <ContainerTitle>{gameName || "The floor is lava"}</ContainerTitle>
        <GameInfoTitle>
          {ro ? "Runda" : "Round"} {round} | Best of{" "}
          {winsNeeded === 3 ? 5 : winsNeeded === 2 ? 3 : 1}
        </GameInfoTitle>
        <ScoreChips>
          <img src="/images/yellowLife.png" alt="yellow" />
          <p>{scoreStatus.yellowTeam}</p>
          <p>/</p>
          <img src="/images/greenLife.png" alt="green" />
          <p>{scoreStatus.greenTeam}</p>
        </ScoreChips>
        <FloorStatusLine $online={floorOnline}>
          ●{" "}
          {floorOnline
            ? `${ro ? "Podea conectată" : "Floor connected"}${
                floor?.sim ? " — SIM" : ""
              }`
            : authFailed
            ? ro
              ? "Token greșit — verifică ⚙"
              : "Wrong token — check ⚙"
            : ro
            ? "Podea deconectată"
            : "Floor offline"}
        </FloorStatusLine>
      </HeaderTitlesContainer>

      {!fixed && showSettings && (
        <FloorSettings ro={ro} onSaved={onSettingsSaved} />
      )}

      <MiddleContainer>
        {phase === "level" && (
          <>
            <SubInfo>
              {ro ? "Nivelul rundei" : "Level for this round"}
            </SubInfo>
            <LevelSelectorContainer>
              {/* lava QR match: the QR fixed the level — nothing to choose */}
              {!fixed && (
                <LevelArrow
                  onClick={() =>
                    setLevelIdx((levelIdx + levels.length - 1) % levels.length)
                  }
                >
                  ‹
                </LevelArrow>
              )}
              <LevelName>{levels[levelIdx]}</LevelName>
              {!fixed && (
                <LevelArrow
                  onClick={() => setLevelIdx((levelIdx + 1) % levels.length)}
                >
                  ›
                </LevelArrow>
              )}
            </LevelSelectorContainer>
            <SubInfo>
              {ro
                ? "Ambele echipe joacă același nivel — timpul cel mai mic câștigă"
                : "Both teams play the same level — the lowest time wins"}
            </SubInfo>
          </>
        )}

        {phase === "team" && (
          <>
            <SubInfo>
              {ro ? "Nivel" : "Level"}: <b>{levels[levelIdx]}</b> —{" "}
              {ro ? "cine joacă acum?" : "who plays now?"}
            </SubInfo>
            <TeamsRow>
              <TeamCard
                $selected={currentTeam === "yellow"}
                $disabled={times.yellow != null}
                onClick={() => setCurrentTeam("yellow")}
              >
                <img src="/images/yellowLife.png" alt="yellow" />
                <p>{teamName("yellow")}</p>
                {times.yellow != null && <span>{times.yellow}s</span>}
              </TeamCard>
              <TeamCard
                $selected={currentTeam === "green"}
                $disabled={times.green != null}
                onClick={() => setCurrentTeam("green")}
              >
                <img src="/images/greenLife.png" alt="green" />
                <p>{teamName("green")}</p>
                {times.green != null && <span>{times.green}s</span>}
              </TeamCard>
            </TeamsRow>
          </>
        )}

        {phase === "playing" && currentTeam && (
          <>
            <SubInfo>
              {ro ? "Pe podea" : "On the floor"}: <b>{teamName(currentTeam)}</b>{" "}
              — {levels[levelIdx]}
            </SubInfo>
            {floor?.state === "countdown" ? (
              <BigTime>3·2·1</BigTime>
            ) : liveTime ? (
              <BigTime>{liveTime}</BigTime>
            ) : (
              <BigTime>…</BigTime>
            )}
            <SubInfo>
              {floor?.state === "countdown"
                ? ro
                  ? "Numărătoarea pe podea — pregătiți-vă!"
                  : "Countdown on the floor — get ready!"
                : floor?.state === "running" && floor.stats
                ? String(floor.stats["penalties"] || "")
                : ro
                ? "Se pornește podeaua…"
                : "Starting the floor…"}
            </SubInfo>
            {floor?.state === "running" && floor.stats && floor.stats["goal"] && (
              <SubInfo>
                {ro ? "Obiectiv" : "Goal"}: {String(floor.stats["goal"])}
              </SubInfo>
            )}
          </>
        )}

        {phase === "roundResult" && (
          <>
            <SubInfo>
              {ro ? "Rezultatul rundei" : "Round result"} {round} —{" "}
              {levels[levelIdx]}
            </SubInfo>
            <TeamsRow>
              <TeamCard $selected={roundWinner === "yellow"}>
                <img src="/images/yellowLife.png" alt="yellow" />
                <p>{teamName("yellow")}</p>
                <span>{times.yellow}s</span>
              </TeamCard>
              <TeamCard $selected={roundWinner === "green"}>
                <img src="/images/greenLife.png" alt="green" />
                <p>{teamName("green")}</p>
                <span>{times.green}s</span>
              </TeamCard>
            </TeamsRow>
            <ContainerTitle>
              {roundWinner === "draw"
                ? ro
                  ? "Remiză!"
                  : "Draw!"
                : `${teamName(roundWinner as TeamKey)} ${
                    ro ? "câștigă runda!" : "wins the round!"
                  }`}
            </ContainerTitle>
          </>
        )}

        <Message>{message}</Message>
      </MiddleContainer>

      <ButtonsContainer>
        {phase === "level" && (
          <PrimaryButton onClick={() => setPhase("team")}>
            <p>{ro ? "Mai departe" : "Next"}</p>
          </PrimaryButton>
        )}
        {phase === "team" && (
          <PrimaryButton
            disabled={!currentTeam || !floorOnline}
            onClick={startRun}
          >
            <p>Start</p>
          </PrimaryButton>
        )}
        {phase === "roundResult" && (
          <>
            <PrimaryButton onClick={handleNextRound}>
              <p>{ro ? "Continuă" : "Continue"}</p>
            </PrimaryButton>
            {/* lava QR match: a played round is final — no replay */}
            {!fixed && (
              <SecondaryButton onClick={replayRound}>
                <p>{ro ? "Rejoacă runda" : "Replay the round"}</p>
              </SecondaryButton>
            )}
          </>
        )}
      </ButtonsContainer>
    </LavaFloorPageContainer>
  );
};
